import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useToast,
    Button,
    Text,
    useColorModeValue,
    VStack,
    Tooltip,
  } from "@chakra-ui/react";
  import React, { useState, useEffect } from "react";
  import axios from "axios";
  import { FiTrash } from "react-icons/fi";
  import { StudentInfo } from "../Common/types";
  import Select from "react-select";
  
  interface BatchStudentMappingProps {
    batch_id: number;
    tenant_id: number;
  }
  
  const BatchStudentMapping: React.FC<BatchStudentMappingProps> = ({ batch_id, tenant_id }) => {
    const [batchStudents, setBatchStudents] = useState<StudentInfo[]>([]);
    const [tenantStudents, setTenantStudents] = useState<StudentInfo[]>([]);
    const [selectedStudents, setSelectedStudents] = useState<number[]>([]); // Store selected student IDs

    const toast = useToast();
    const tableHeaderBg = useColorModeValue("teal.100", "teal.700");
    const buttonBg = useColorModeValue("red.500", "red.700");
    const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
  
    // Fetch students in batch
    const fetchBatchStudents = async () => {
      try {
        const response = await axios.get(`${apiurl}/batches/${batch_id}/students`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setBatchStudents(response.data);
      } catch (error) {
        console.error("Failed to load students", error);
        toast({
          title: "Error fetching students",
          description: "Failed to load student data.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

     // Fetch students in batch
     const fetchTenantStudents = async () => {
        try {
            
            const response = await axios.get(
                `${apiurl}/tenants/${tenant_id}/students-not-in-batch`, 
                {
                  params: { batch_id }, // Add batch_id as a query parameter
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                  },
                }
              );
          setTenantStudents(response.data);
        } catch (error) {
          console.error("Failed to load students", error);
          toast({
            title: "Error fetching students",
            description: "Failed to load student data.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      };

    // Add selected students to the batch
  const addStudentsToBatch = async () => {
    try {
      for (const studentId of selectedStudents) {
        await axios.post(
          `${apiurl}/batches/${batch_id}/add-student`,
          { student_id: studentId },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }
      toast({
        title: "Students Added",
        description: "Selected students have been added to the batch.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setSelectedStudents([]); // Reset selection
      fetchBatchStudents();
      fetchTenantStudents(); // Refresh tenant students
    } catch (error) {
      console.error("Failed to add students", error);
      toast({
        title: "Error adding students",
        description: "Failed to add selected students to the batch.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

    // Format options for react-select
    const studentOptions = tenantStudents.map((student) => ({
        value: student.student_id,
        label: `${student.student_id} | ${student.givenName} ${student.surname} | ${student.email}`,
      }));
  
    // Handle student deletion
    const handleDeleteStudent = async (student_id: number) => {
      try {
        await axios.delete(`${apiurl}/batches/${batch_id}/students/${student_id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
  
        // Remove the student from the state
        setBatchStudents((prev) => prev.filter((student) => student.student_id !== student_id));
        fetchTenantStudents();
  
        toast({
          title: "Student removed",
          description: `Student ID ${student_id} has been removed successfully.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error("Failed to remove student", error);
        toast({
          title: "Error removing student",
          description: "Failed to remove the student. Try again later.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };
  
    useEffect(() => {
      fetchBatchStudents();
      fetchTenantStudents();
    }, []);
  
    return (
      <Box >

    <Box borderRadius={"sm"} borderWidth={"1px"} p={4}>
    <VStack spacing={4} align="start">
    <Text  fontSize="lg" fontWeight="bold" >
          Select Student from Overall Tenant Space
        </Text>
      <Box width="100%">
        <Select
          isMulti
          options={studentOptions}
          value={studentOptions.filter(option => selectedStudents.includes(option.value))} // Make sure selected students are in the value list
          onChange={(selectedOptions) => setSelectedStudents(selectedOptions.map((opt) => opt.value))}
          placeholder="Select students to add..."
          closeMenuOnSelect={false}
        />
      </Box>
      <Button mt={10} borderRadius={"sm"} fontSize={"sm"} colorScheme="blue" onClick={addStudentsToBatch} isDisabled={selectedStudents.length === 0}>
        Add Students to Batch
      </Button>
    </VStack>
        
    </Box>

    <Box mt={2}borderRadius={"sm"} borderWidth={"1px"} p={4}>
        <Text  fontSize="lg" fontWeight="bold" mb="4">
          Students in Batch #{batch_id}
        </Text>
  
        <Table variant="simple" size="sm">
          <Thead bg={tableHeaderBg}>
            <Tr>
              <Th>Student ID</Th>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Contact No</Th>
              <Th>School</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {batchStudents.map((student) => (
              <Tr key={student.student_id}>
                <Td>{student.student_id}</Td>
                <Td>
                  {student.givenName} {student.middlename} {student.surname}
                </Td>
                <Td>{student.email}</Td>
                <Td>{student.contactNo}</Td>
                <Td>{student.schoolName}</Td>
                <Td>
                  <Tooltip label="Remove Student" placement="top">
                    <Button
                      size="sm"
                      bg={buttonBg}
                      color="white"
                      _hover={{ bg: "red.600" }}
                      onClick={() => handleDeleteStudent(student.student_id)}
                    >
                      <FiTrash />
                    </Button>
                  </Tooltip>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        </Box>
      </Box>
    );
  };
  
  export default BatchStudentMapping;
  